'use client';

/**
 * Floating "AI is working" indicator.
 *
 * Rendered above ALL layers (highest modal is z-[200]) so the user always
 * sees that the assistant is busy — e.g. while it opens a dialog and
 * typewriter-fills fields behind a modal backdrop. Sits at the orb position
 * (bottom-right); pointer-events-none so it never blocks clicks.
 *
 * Portaled to document.body: Headless UI dialogs portal there too, so an
 * in-tree fixed element can get trapped in an ancestor stacking context
 * below the modal — at body level, z-[300] reliably wins over z-[100/200].
 *
 * Never shown while the chat panel is open — the panel's header icon and
 * send button animate instead.
 *
 * A short delay before showing avoids flashing on sub-300ms operations.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAiAssistant } from '@/lib/platform/contexts/ai-assistant-context';
import AiWorkingStar from './AiWorkingStar';

const SHOW_DELAY_MS = 300;

export default function AiWorkingIndicator() {
  const { working, isOpen } = useAiAssistant();
  // Never shown while the chat panel is open — the panel's header icon and
  // send button are the activity signals there.
  const show = working && !isOpen;
  const [visible, setVisible] = useState(false);

  // Hide immediately when work stops (adjust-state-during-render pattern)
  const [prevShow, setPrevShow] = useState(show);
  if (prevShow !== show) {
    setPrevShow(show);
    if (!show) setVisible(false);
  }

  // Show after a short delay so sub-300ms operations don't flash
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [show]);

  // visible only becomes true via the effect, so document always exists here
  if (!visible) return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-label="AI assistant is working"
      className="pointer-events-none fixed right-2 bottom-5 z-[300] flex h-14 w-14 items-center justify-center md:right-6 md:bottom-6 print:hidden"
    >
      <div className="ai-working-glow flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg">
        <AiWorkingStar className="h-6 w-6" />
      </div>
    </div>,
    document.body
  );
}
